import React, { useEffect, useMemo, useState } from 'react';

import { LinearProgress, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import NotificationsIcon from '@material-ui/icons/Notifications';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import { format, isPast } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import DialogViewWrapper from 'components/DialogViewWrapper';
import InformationSummary from 'components/InformationSummary';
import Loader from 'components/Loader';
import RichTextContent from 'components/RichTextContent/RichTextContent';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import action_library_messages from 'messages/action_library_messages';
import assets_messages from 'messages/assets_messages';
import PATHS from 'router/PATHS';

import ActionLibraryInstanceActions from './_components/ActionLibraryInstanceActions/ActionLibraryInstanceActions';
import ActionLibraryInstanceDetailsSteps from './_components/ActionLibraryInstanceDetailsSteps/ActionLibraryInstanceDetailsSteps';
import useStyles from './ActionLibraryInstanceDetailPage.styles';

const ActionLibraryInstanceDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [instance, setInstance] = useState<ActionLibraryInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const styles = useStyles();

  useEffect(() => {
    const loadInstance = async () => {
      try {
        setLoading(true);
        const { data } = await actionLibraryInstancesApi.getSingleActionLibraryInstance(Number(id));
        setInstance(data);
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadInstance();
    }
  }, [id]);

  const handleBack = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  };

  const handleDelete = () => {
    history.push(PATHS.ACTION_LIBRARY_INSTANCE);
  };

  const informationSummaryData = useMemo(() => {
    if (!instance) return [];

    const deadlineDate = instance.deadline ? new Date(instance.deadline) : null;
    const reminderDate = instance.reminder ? new Date(instance.reminder) : null;
    const isDeadlinePast = deadlineDate && isPast(deadlineDate) && instance.status !== 'completed';
    const isReminderPast = reminderDate && isPast(reminderDate) && instance.status !== 'completed';

    // Status with icon
    let StatusIconComponent;
    let statusIconClass;
    if (instance.status === 'completed') {
      StatusIconComponent = CheckCircleIcon;
      statusIconClass = styles.activeIcon;
    } else if (instance.status === 'in_progress') {
      StatusIconComponent = RemoveCircleOutlineIcon;
      statusIconClass = styles.criticalityMediumIcon;
    } else {
      StatusIconComponent = CancelIcon;
      statusIconClass = styles.inactiveIcon;
    }

    // Criticality with icon
    let CriticalityIconComponent;
    let criticalityIconClass;
    if (instance.criticality === 'low') {
      CriticalityIconComponent = InfoIcon;
      criticalityIconClass = styles.criticalityLowIcon;
    } else if (instance.criticality === 'medium') {
      CriticalityIconComponent = WarningIcon;
      criticalityIconClass = styles.criticalityMediumIcon;
    } else {
      CriticalityIconComponent = ErrorIcon;
      criticalityIconClass = styles.criticalityHighIcon;
    }

    const criticalityKey = instance.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';

    return [
      {
        label: action_library_messages.instance.detail.status,
        value: (
          <div className={styles.valueWithIcon}>
            <StatusIconComponent className={`${styles.statusIcon} ${statusIconClass}`} />
            <span>{instance.status}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.instance.detail.status_percent,
        value: (() => {
          const percent = Math.min(100, Math.max(0, instance.statusPercent || 0));
          let progressColorClass: string;
          if (percent >= 75) {
            progressColorClass = styles.progressColorHigh;
          } else if (percent >= 50) {
            progressColorClass = styles.progressColorMedium;
          } else if (percent >= 25) {
            progressColorClass = styles.progressColorLow;
          } else {
            progressColorClass = styles.progressColorVeryLow;
          }

          return (
            <div className={styles.progressContainer}>
              <div className={styles.progressBarWrapper}>
                <LinearProgress className={`${styles.progressBar} ${progressColorClass}`} value={percent} variant='determinate' />
              </div>
              <Typography className={styles.progressPercentage} variant='body2'>
                {percent}%
              </Typography>
            </div>
          );
        })(),
      },
      {
        label: action_library_messages.instance.detail.criticality,
        value: (
          <div className={styles.valueWithIcon}>
            <CriticalityIconComponent className={`${styles.statusIcon} ${criticalityIconClass}`} />
            <span>{t(action_library_messages.criticality[criticalityKey])}</span>
          </div>
        ),
      },
      {
        label: action_library_messages.instance.detail.asset_name,
        value: instance.assetName || '-',
      },
      {
        label: action_library_messages.instance.detail.asset_type,
        value: instance.assetType || '-',
      },
      {
        label: action_library_messages.instance.detail.asset_importance,
        value: instance.assetImportance
          ? // @ts-ignore
            t(assets_messages.information_choices_assets_importance[instance.assetImportance]) || instance.assetImportance
          : '-',
      },
      {
        label: action_library_messages.instance.detail.owner,
        value: instance.ownerFullName || '-',
      },
      ...(instance.delegatedToFullName
        ? [
            {
              label: action_library_messages.instance.detail.delegated_to,
              value: instance.delegatedToFullName,
            },
          ]
        : []),
      {
        label: action_library_messages.instance.detail.deadline,
        value: deadlineDate ? (
          <div className={styles.deadlineContainer}>
            <span className={isDeadlinePast ? styles.deadlineTextPast : styles.deadlineText}>
              {format(deadlineDate, 'yyyy-MM-dd HH:mm')}
            </span>
          </div>
        ) : (
          '-'
        ),
      },
      {
        label: action_library_messages.instance.detail.reminder,
        value: reminderDate ? (
          <div className={styles.reminderContainer}>
            <NotificationsIcon className={isReminderPast ? styles.reminderIconPast : styles.reminderIcon} />
            <span className={isReminderPast ? styles.reminderTextPast : styles.reminderText}>
              {format(reminderDate, 'yyyy-MM-dd HH:mm')}
            </span>
          </div>
        ) : (
          '-'
        ),
      },
    ];
  }, [instance, t, styles]);

  if (loading) {
    return (
      <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
        <Loader inner />
      </DialogViewWrapper>
    );
  }

  if (error || !instance) {
    return (
      <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
        <div>{t(action_library_messages.error_state.title)}</div>
      </DialogViewWrapper>
    );
  }

  return (
    <DialogViewWrapper contentSize='lg' onBack={handleBack} title={t(action_library_messages.instance.detail.page_title)}>
      <div className={styles.stickyMenuContainer}>
        <ActionLibraryInstanceActions instance={instance} onDelete={handleDelete} />
      </div>
      <CenteredGrid iconLeft={null} tight title={instance.title} width='lg' withoutPadding />
      <CenteredGrid gridGap={2} tight width='lg'>
        <Typography className={styles.descriptionText} variant='body2'>
          <RichTextContent content={instance.description || '-'} />
        </Typography>
      </CenteredGrid>

      <CenteredGrid gridGap={2} tight title='' width='lg'>
        <InformationSummary data={informationSummaryData} />
      </CenteredGrid>

      {instance.specialInstruction && (
        <CenteredGrid gridGap={2} tight title={t(action_library_messages.instance.detail.special_instruction)} width='lg'>
          <Typography variant='body2'>
            <RichTextContent content={instance.specialInstruction} />
          </Typography>
        </CenteredGrid>
      )}

      {instance.steps && instance.steps.length > 0 && (
        <CenteredGrid gridGap={2} id='action-instance-steps-section' tight title={t(action_library_messages.detail.steps)} width='lg'>
          <ActionLibraryInstanceDetailsSteps steps={instance.steps} />
        </CenteredGrid>
      )}
    </DialogViewWrapper>
  );
};

export default ActionLibraryInstanceDetailPage;
